import React, { useEffect, useState } from 'react';
import { Snackbar } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Mock from '../../constants/Mock';
import {
  ICountry,
  ICountryEmailRecipient,
  ICountryEmailRecipientCreatePayload,
  ICountryEmailRecipientUpdatePayload,
} from '../../interfaces/Country.interface';
import {
  IColumnTable,
  ICountryEmailRecipientDataTable,
} from '../../interfaces/Table.interface';
import { IFormData, IFormSchema } from '../../interfaces/Form-Modal.interface';
import { IPagination } from '../../interfaces/Pagination.interface';
import { IUser } from '../../interfaces/User.interface';
import AddEditModal from '../../shared/components/add-edit-modal/Add-Edit-Modal';
import ConfirmDialog from '../../shared/components/confirm-dialog/Confirm-Dialog';
import Loader from '../../shared/components/Loader/Loader';
import Stats from '../../shared/components/stats/Stats';
import ButtonComponent from '../../shared/components/button/Button';
import IconStatusRejected from './../../assets/icons/icon-status-rejected.svg';
import IconCross from './../../assets/icons/icon-cross.svg';
import { useSnackbar } from '../../services/SnackbarUtils';
import {
  GetCountry,
  createCountryEmailRecipient,
  deleteCountryEmailRecipient,
  editCountryEmailRecipient,
  getCountryEmailRecipientList,
} from '../../services/Country.service';
import { isManagerOrAdmin } from '../../utils/role';
import './CountryDetails.scss';

interface ICountryDetailsProps {
  currentUser: IUser;
}

const emailRecipientFormSchema: IFormSchema = Yup.object({
  email: Yup.string().email().required(),
});

const CountryDetails = (props: ICountryDetailsProps): JSX.Element => {
  const { t } = useTranslation('common');
  const params = useParams();

  const isAllowed = isManagerOrAdmin(props.currentUser);

  // UI State
  const [openEmailRecipientModal, setOpenEmailRecipientModal] = useState(false);
  const [openDeleteEmailRecipientConfirmModal, setOpenDeleteEmailRecipientConfirmModal] =
    useState(false);
  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>();

  const { snackBarOpen, snackbarMessage, snackBarType, showSnackbar, handleSnackBarClose } =
    useSnackbar();

  const [country, setCountry] = useState<ICountry>();
  const [emailRecipientsSearchValue, _setEmailRecipientsSearchValue] = useState('');
  const [emailRecipientsDataTable, setEmailRecipientsDataTable] = useState<
    ICountryEmailRecipientDataTable[]
  >([]);
  const [emailRecipientsListPagination, setEmailRecipientsListPagination] =
    useState<IPagination>();
  const [selectedEmailRecipient, setSelectedEmailRecipient] =
    useState<ICountryEmailRecipient | null>(null);

  const { EmailRecipientModalForm } = Mock();
  const [CountryEmailRecipientForm] = useState<IFormData[]>(
    JSON.parse(JSON.stringify(EmailRecipientModalForm))
  );

  const emailRecipientColumnsTable: IColumnTable[] = [
    { accessorKey: 'email', header: t('email') },
    {
      accessorKey: 'action',
      header: t('action'),
      Cell: ({ row }: any) => {
        return (
          <div className="status-container">
            <ButtonComponent
              iconUrl={IconStatusRejected}
              iconAlt="icon-delete"
              iconOnly={true}
              iconRound={true}
              label="Delete"
              color="default"
              onClick={() => {
                handleDeleteEmailRecipient(row.original.id, row.original.email);
              }}
            />
          </div>
        );
      },
    },
  ];

  const fetchCountry = (): void => {
    GetCountry(params.id as string)
      .then((countryData) => {
        setCountry(countryData);
      })
      .catch((error) => {
        showSnackbar(error.message, 'error');
      });
  };

  const fetchCountryEmailRecipientList = (offset: number): void => {
    setIsRequestInProgress(true);
    getCountryEmailRecipientList(params.id as string, 10, offset)
      .then((emailRecipientList) => {
        setIsRequestInProgress(false);
        setEmailRecipientsListPagination(emailRecipientList.pagination);
        getEmailRecipientsDataTable(emailRecipientList.data);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const handleEmailRecipientSubmit = (formData: IFormSchema): void => {
    setIsRequestInProgress(true);

    if (selectedEmailRecipient !== null) {
      const payload: ICountryEmailRecipientUpdatePayload = {
        email: formData.email,
      };

      editCountryEmailRecipient(selectedEmailRecipient.id, payload)
        .then(() => {
          setIsRequestInProgress(false);
          setOpenEmailRecipientModal(false);
          setSelectedEmailRecipient(null);
          showSnackbar(t('email_recipient_has_been_edited_successfully'), 'success');
          fetchCountryEmailRecipientList(0);
        })
        .catch((error) => {
          setIsRequestInProgress(false);
          showSnackbar(error.message, 'error');
        });

      return;
    }

    const payload: ICountryEmailRecipientCreatePayload = {
      country_id: params.id as string,
      email: formData.email,
    };

    createCountryEmailRecipient(payload)
      .then(() => {
        setIsRequestInProgress(false);
        setOpenEmailRecipientModal(false);
        showSnackbar(t('email_recipient_has_been_created_successfully'), 'success');
        fetchCountryEmailRecipientList(0);
      })
      .catch((error) => {
        setIsRequestInProgress(false);
        showSnackbar(error.message, 'error');
      });
  };

  const handleEmailRecipientTablePaginationChange = (tablePageIndex: number): void => {
    const offset = tablePageIndex * 10;
    fetchCountryEmailRecipientList(offset);
  };

  const getEmailRecipientsDataTable = (
    emailRecipientsData: ICountryEmailRecipient[]
  ): void => {
    const emailRecipientsTable: ICountryEmailRecipientDataTable[] =
      emailRecipientsData.map((recipient) => ({
        id: recipient.id,
        email: recipient.email,
      }));

    setEmailRecipientsDataTable(emailRecipientsTable);
  };

  const openAddEmailRecipientModal = (): void => {
    setSelectedEmailRecipient(null);
    setOpenEmailRecipientModal(true);
  };

  const emailRecipientModalClose = (): void => {
    setOpenEmailRecipientModal(false);
    setSelectedEmailRecipient(null);
  };

  const emailRecipientsRowClicked = (): void => {};

  const handleDeleteEmailRecipient = (id: string, email: string): void => {
    setSelectedEmailRecipient({ id, email });
    setOpenDeleteEmailRecipientConfirmModal(true);
  };

  useEffect(() => {
    if (isAllowed) {
      fetchCountry();
      fetchCountryEmailRecipientList(0);
    }
  }, [isAllowed]);

  const setEmailRecipientHeaderButtons = (): React.JSX.Element => {
    return (
      <ButtonComponent
        iconUrl={IconCross}
        iconAlt="icon-cross"
        label={t('add_email_recipient')}
        color="primary"
        onClick={openAddEmailRecipientModal}
      />
    );
  };

  const getEmptyMessage = (): string => {
    if (isRequestInProgress !== false) {
      return '';
    }

    return `${String(t('no_email_recipients_to_display'))} ${String(
      t('country_email_recipient_empty_warning')
    )}`;
  };

  if (!isAllowed) {
    return (
      <div className="country-details">
        <p className="not-authorized">{t('not_authorized_to_view_this_section')}</p>
      </div>
    );
  }

  return (
    <div className="country-details">
      {isRequestInProgress === true && <Loader />}

      <Stats
        displayHeaderLeftSideBlock={true}
        title={country !== undefined ? country.name : t('country')}
        subtitle={t('country_email_recipients_subtitle')}
        searchValue={emailRecipientsSearchValue}
        headerButtons={setEmailRecipientHeaderButtons()}
        dataTable={emailRecipientsDataTable}
        columnsTable={emailRecipientColumnsTable}
        emptyMsgTable={getEmptyMessage()}
        onTableRowClicked={emailRecipientsRowClicked}
        handleTablePaginationChange={handleEmailRecipientTablePaginationChange}
        tableTotalPages={
          emailRecipientsListPagination !== undefined
            ? emailRecipientsListPagination.totalPages
            : 0
        }
        searchPlaceholder={t('email_recipients_search_placeholder')}
      />

      <Snackbar
        className={`oryx-snackbar ${snackBarType}`}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackBarOpen}
        message={snackbarMessage}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}
      />

      <AddEditModal
        modalTitle={
          selectedEmailRecipient !== null
            ? t('edit_email_recipient')
            : t('add_email_recipient')
        }
        modalForm={CountryEmailRecipientForm}
        formSchema={emailRecipientFormSchema}
        formInitialValues={{
          email: selectedEmailRecipient !== null ? selectedEmailRecipient.email : '',
        }}
        openModal={openEmailRecipientModal}
        onClose={emailRecipientModalClose}
        handleSubmit={handleEmailRecipientSubmit}
        closeConfirmationModalTitle={t('are_you_sure')}
        closeConfirmationModalDescription={t('email_recipient_info_will_be_lost')}
      />

      <ConfirmDialog
        openModal={openDeleteEmailRecipientConfirmModal}
        title={t('email_recipient_delete_modal_title')}
        description={t('email_recipient_delete_modal_description')}
        confirmLabel={t('email_recipient_delete_modal_label')}
        cancelLabel={t('cancel')}
        confirmButtonColor={'inactive'}
        handleConfirm={() => {
          if (selectedEmailRecipient !== null) {
            setIsRequestInProgress(true);
            void deleteCountryEmailRecipient(selectedEmailRecipient.id)
              .then(() => {
                setIsRequestInProgress(false);
                showSnackbar(t('email_recipient_has_been_deleted_successfully'), 'success');
                fetchCountryEmailRecipientList(0);
              })
              .catch((error) => {
                setIsRequestInProgress(false);
                showSnackbar(error.message, 'error');
              });
          }
          setOpenDeleteEmailRecipientConfirmModal(false);
          setSelectedEmailRecipient(null);
        }}
        handleClose={() => {
          setOpenDeleteEmailRecipientConfirmModal(false);
          setSelectedEmailRecipient(null);
        }}
      />
    </div>
  );
};

export default CountryDetails;
